import React from 'react';

import { SLASyncOption } from '@ariksa/notification/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { customTheme } from 'theme';

import { WithResourceIcon } from 'components/DataDisplay';

// Fixed: register the duration plugin used by convertSlaMinutes here; it was
// only registered as a side effect of another module, so dayjs.duration
// could be undefined when this file loaded first.
dayjs.extend(duration);

export const renderOrgName = name => (
  <HStack spacing={4} pl={4}>
    <Box
      color={customTheme.colors.gray[300]}
      fontWeight={400}
      whiteSpace="nowrap"
    >
      Organization Name
    </Box>
    <Box
      w="50%"
      border="1px solid"
      borderRadius={6}
      borderColor={customTheme.colors.gray[100]}
      bg={customTheme.colors.gray[50]}
      px={3}
      py={0.5}
    >
      {name}
    </Box>
  </HStack>
);

export const renderOrgSettingHeader = ({
  icon,
  header,
  subHeader,
  color = customTheme.colors.primary,
  rightComponent,
}) => (
  <WithResourceIcon
    icon={icon}
    border={false}
    hStackStyles={{ align: 'flex-start' }}
    color={color}
    iconStyles={{ w: 10, h: 10, p: 1.5 }}
  >
    <Stack spacing={rightComponent ? 0 : 1} pt={rightComponent ? 1 : 2}>
      <HStack justify="space-between">
        <Box fontWeight={600}>{header}</Box>
        {rightComponent}
      </HStack>
      <Box fontSize="sm" color={customTheme.colors.gray[300]}>
        {subHeader}
      </Box>
    </Stack>
  </WithResourceIcon>
);

export const orgSettingsIntervalOptions = [
  { label: '3 hours', value: 3 * 60 },
  { label: '6 hours', value: 6 * 60 },
  { label: '12 hours', value: 12 * 60 },
  { label: '24 hours', value: 24 * 60 },
];

export const convertSlaMinutes = minutes => {
  const totalMinutes = Number(minutes);
  if (isNaN(totalMinutes) || totalMinutes < 0) {
    console.log('Not a valid number');
    return;
  }

  const d = dayjs.duration(totalMinutes, 'minutes');
  const days = Math.floor(d.asDays());
  const hours = Math.floor(d.asHours() % 24);
  const mins = Math.floor(d.asMinutes() % 60);

  let formatted = '';
  if (days > 0) formatted += `${days} day${days > 1 ? 's' : ''} `;
  // Fixed: round leftover minutes up to an hour even when there are no whole
  // hours (30 minutes showed "0 hour", 1 day 30 minutes showed "1 day").
  const roundedHours = hours + (mins > 0 ? 1 : 0);
  if (roundedHours > 0)
    formatted += `${roundedHours} hour${roundedHours > 1 ? 's' : ''} `;

  return formatted.trim() || '0 hour';
};

// Label for a custom SLA option, e.g. "1 day, 2 hours". Added to replace the
// inline label in AvailableSLAsModal, which produced "1 days,2 hours".
export const formatSlaLabel = (days?: number, hours?: number) =>
  [
    days ? `${days} day${days > 1 ? 's' : ''}` : '',
    hours ? `${hours} hour${hours > 1 ? 's' : ''}` : '',
  ]
    .filter(Boolean)
    .join(', ');

export const slaSyncOptions = [
  {
    label: 'Apply SLAs to only future issues and tickets',
    description:
      'Existing issues and tickets with due dates will not be edited based on SLAs.',
    key: SLASyncOption.NoSync,
  },
  {
    label: 'Only update issues and tickets with no existing due date',
    description:
      'Apply SLAs to only issues that don’t currently have any due date' +
      ' configured. Issues that already have due dates will not be ' +
      'modified and any associated tickets will not be edited.',
    key: SLASyncOption.NoExistingSla,
  },
  {
    label: 'Update due date for all issues and tickets',
    description:
      'Apply the new SLAs to calculate due date for all ' +
      'existing issues, overriding any existing due dates. Tickets ' +
      'for these issues will be updated with the new due date as well.',
    key: SLASyncOption.UpdateAll,
  },
];
